import { getLegacyFinixSDK } from '../../services/cxPublicFinixService';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useReviewParams, useAppointmentDetails, useStepNavigation } from '../organisms/ReviewWrapper';
import ApplePayButton from '../atoms/ApplePayButton';
import PoweredByApruv from '../common/PoweredByApruv';
import './PaymentMethod.css';
import { Dialog } from 'primereact/dialog';
import { Button as PrimeButton } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';

// Remove the following block:
// declare namespace JSX {
//   interface IntrinsicElements {
//     'apple-pay-button': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
//   }
// }
// If you need to extend JSX.IntrinsicElements, do so in a .d.ts file using ES module syntax.

declare global {
  interface Window {
    Finix?: any;
    google?: any;
    ApplePaySession?: any;
  }
}

// PaymentMethod.tsx - Unified payment page for Finix Card, Google Pay, Apple Pay
// This component matches the Angular payment-method.component.ts logic

// TODO: Add any necessary imports for API calls, context, and design tokens

const PROCESSING_FEE_FIXED = 0.31;
const PROCESSING_FEE_PERCENT = 0.029;

function calculateProcessingFee(tip: number) {
  return ((tip + PROCESSING_FEE_FIXED) / (1 - PROCESSING_FEE_PERCENT)) - tip;
}

// Privacy Policy Content Component
const PrivacyPolicyContent: React.FC = () => (
  <div className="text-sm text-gray-700 max-h-[60vh] overflow-y-auto pr-2 leading-relaxed space-y-4">
    <h2 className="font-bold text-xl mb-4">Privacy Policy for APRUV</h2>
    <p className="mb-2">Last Updated: 9th August 2023</p>
    <p>Welcome to APRUV. Your privacy is of utmost importance to us. This Privacy Policy explains what information we collect, how we use it, and your rights and choices regarding this information. By using our services, you agree to the terms of this Privacy Policy.</p>
    <h3 className="font-semibold text-lg mt-6">1. Introduction</h3>
    <p>APRUV integrates with service management companies to facilitate the sending of review requests and tip options to clients. This policy is tailored to explain how we manage and safeguard data collected through this integration.</p>
    <h3 className="font-semibold text-lg mt-6">2. Information We Collect</h3>
    <ul className="list-disc ml-6">
      <li>Automatically from Service Management Companies: This may include customer names, phone numbers, email addresses, service details, and service dates.</li>
      <li>Directly from Users: When users respond to review requests, provide feedback, or choose to leave a tip.</li>
      <li>From Cookies and Similar Technologies: Information about your device, IP address, browsing history on our platform, and how you interact with our services.</li>
    </ul>
    <h3 className="font-semibold text-lg mt-6">3. How We Use the Information</h3>
    <ul className="list-disc ml-6">
      <li>Send text and email review requests.</li>
      <li>Facilitate the tip leaving process.</li>
      <li>Improve our services, including troubleshooting, data analysis, and research.</li>
      <li>Comply with legal requirements and protect the rights, safety, and property of APRUV, our users, and the public.</li>
    </ul>
    <h3 className="font-semibold text-lg mt-6">4. Sharing of Information</h3>
    <ul className="list-disc ml-6">
      <li>We do not sell your personal information. We may share information:</li>
      <li>With service providers who help us run our services, but they cannot use your information for their own purposes without your consent.</li>
      <li>If required by law, in connection with any legal proceedings, and to establish, exercise, or defend our legal rights.</li>
      <li>In connection with a sale, merger, or acquisition of all or part of APRUV.</li>
    </ul>
    <h3 className="font-semibold text-lg mt-6">5. Data Retention</h3>
    <p>We keep your data for as long as it's necessary to fulfill the purposes for which it was collected or to comply with legal, regulatory, or internal policy requirements.</p>
    <h3 className="font-semibold text-lg mt-6">6. Your Rights and Choices</h3>
    <ul className="list-disc ml-6">
      <li>Request access to your personal information.</li>
      <li>Ask for corrections, updates, or deletion of your data.</li>
      <li>Opt-out of receiving review requests.</li>
      <li>Raise concerns about our use of your data.</li>
    </ul>
    <h3 className="font-semibold text-lg mt-6">7. Security</h3>
    <p>We implement stringent security measures to protect your data from unauthorized access, alteration, disclosure, or destruction. These measures include encryption, secure servers, and regular audits.</p>
    <h3 className="font-semibold text-lg mt-6">8. Cookies and Similar Technologies</h3>
    <p>We use cookies and similar technologies to enhance user experience, for analytics, and for advertisement targeting. You can configure your browser to reject all or some cookies or notify you when cookies are set.</p>
    <h3 className="font-semibold text-lg mt-6">9. Links to Other Websites</h3>
    <p>Our service may contain links to other sites. We're not responsible for the privacy practices of those sites. This policy only applies to information collected by APRUV.</p>
    <h3 className="font-semibold text-lg mt-6">10. Changes to This Privacy Policy</h3>
    <p>We may update this Privacy Policy from time to time. Any changes will be posted on this page with the updated revision date.</p>
    <h3 className="font-semibold text-lg mt-6">11. Contact Us</h3>
    <p>If you have questions or concerns about this Privacy Policy or our practices, please contact us at:</p>
    <p>APRUV Software Company 3971 Ventura Ave Idaho Falls Idaho, 83401 Email: support@apruv.com</p>
  </div>
);

// Terms of Service Content Component
const TermsOfServiceContent: React.FC = () => (
  <div className="text-sm text-gray-700 max-h-[60vh] overflow-y-auto pr-2 leading-relaxed space-y-4">
    <h2 className="font-bold text-xl mb-4">Terms and Conditions for APRUV</h2>
    <p className="mb-2">Last Updated: 9th August 2023</p>
    <p>Welcome to APRUV. By using our services, you are agreeing to the following terms and conditions. Please read them carefully.</p>
    <h3 className="font-semibold text-lg mt-6">1. Acceptance of Terms</h3>
    <p>By accessing or using the APRUV software and services, you accept and agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, you should not use our services.</p>
    <h3 className="font-semibold text-lg mt-6">2. Changes to Terms</h3>
    <p>APRUV reserves the right to modify or replace these Terms at any time. Users will be notified of any changes, and continued use of our services after such changes will constitute acceptance of those changes.</p>
    <h3 className="font-semibold text-lg mt-6">3. Service Description</h3>
    <p>APRUV integrates with service management companies to send review requests and facilitates tipping options. Our services might evolve over time, and the form and nature of these services may change without prior notice.</p>
    <h3 className="font-semibold text-lg mt-6">4. User Responsibilities</h3>
    <ul className="list-disc ml-6">
      <li>Provide accurate information when prompted by our services.</li>
      <li>Use our services in a lawful manner and not for fraudulent or malicious activities.</li>
      <li>Comply with all applicable local, state, and international laws and regulations.</li>
    </ul>
    <h3 className="font-semibold text-lg mt-6">5. Payment Processing</h3>
    <p>APRUV uses Braintree to process payments. When using our services for financial transactions, you agree to be bound by Braintree's Terms and Conditions and their privacy policy. APRUV is not responsible for any issues arising from financial transactions processed by Braintree.</p>
    <h3 className="font-semibold text-lg mt-6">6. Termination</h3>
    <p>APRUV reserves the right to terminate or suspend access to our services, without notice, for behavior that APRUV believes violates these Terms or is harmful to other users, third parties, or the business interests of APRUV.</p>
    <h3 className="font-semibold text-lg mt-6">7. Disclaimers and Limitation of Liability</h3>
    <p>Our services are provided "as is." APRUV makes no guarantees regarding the continuous, uninterrupted or secure access to our services. To the maximum extent permitted by law, APRUV disclaims all warranties, express or implied, including but not limited to implied warranties of merchantability and fitness for a particular purpose.</p>
    <p>In no event will APRUV be liable for any indirect, incidental, special, consequential, or exemplary damages, including but not limited to damages for loss of profits, goodwill, use, data, or other intangible losses, arising out of or in connection with our services or these Terms.</p>
    <h3 className="font-semibold text-lg mt-6">8. Indemnification</h3>
    <p>Users agree to indemnify, defend, and hold harmless APRUV, its affiliates, officers, directors, employees, agents, and licensors from any claim, cost, expense, judgment, or other loss relating to the use of our services, including without limitation of the foregoing, any action you take which is in violation of these Terms.</p>
    <h3 className="font-semibold text-lg mt-6">9. Governing Law</h3>
    <p>These Terms are governed by and construed in accordance with the laws of the jurisdiction in which APRUV operates, without regard to its conflict of law provisions.</p>
    <h3 className="font-semibold text-lg mt-6">10. Feedback and Complaints</h3>
    <p>Any feedback, comments, or suggestions you may provide regarding APRUV is entirely voluntary and we will be free to use such feedback, comments, or suggestions as we see fit without any obligation to you.</p>
    <p>For any complaints, please reach out to us using the contact details provided in our Privacy Policy.</p>
    <h3 className="font-semibold text-lg mt-6">11. Miscellaneous</h3>
    <p>If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary so that the Terms will otherwise remain in full force and effect.</p>
    <h3 className="font-semibold text-lg mt-6">Contact</h3>
    <p>For any queries regarding these Terms, please contact:</p>
    <p>APRUV Software Company 3971 Ventura Ave Idaho Falls Idaho, 83401 Email: support@apruv.com</p>
  </div>
);

const PaymentMethod: React.FC = () => {
  const location = useLocation();
  const { appointmentId, companyName } = useReviewParams();
  const appointmentDetails = useAppointmentDetails();
  const { goToThankYou, goToTipping } = useStepNavigation();

  // State
  const [tip, setTip] = useState<number>(0);
  const [processingFee, setProcessingFee] = useState<number>(0);
  const [isFee, setIsFee] = useState<boolean>(true);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [idempotencyKey, setIdempotencyKey] = useState<string>('');
  const [showPrivacy, setShowPrivacy] = useState<boolean>(false);
  const [showTerms, setShowTerms] = useState<boolean>(false);
  const [sessionKey, setSessionKey] = useState<string>('');
  const [isApplePaySupported, setIsApplePaySupported] = useState(false);

  // On mount, initialize idempotency key
  useEffect(() => {
    setIdempotencyKey(crypto.randomUUID());
    // Set initial tip if present
    if (location.state?.tip) setTip(location.state.tip);
    else setTip(10); // fallback default
    updateProcessingFee(location.state?.tip || 10, isFee);
  }, [appointmentId, companyName, isFee, location.state?.appointmentDetails, location.state.tip]);

  // Set tip from location.state if present
  useEffect(() => {
    if (location.state?.tip) {
      setTip(location.state.tip);
    }
  }, [location.state]);

  // Update processing fee and total when tip or isFee changes
  useEffect(() => {
    updateProcessingFee(tip, isFee);
  }, [tip, isFee]);

  // Finix Card Form Initialization
  useEffect(() => {
    const options = {
      showAddress: true,
      showLabels: true,
      showPlaceholders: true,
      defaultValues: {},
      hideFields: ["name", "address_line1", "address_line2", "address_city", "address_region", "address_state", "address_country"],
      requiredFields: ["address_postal_code"],
      hideErrorMessages: false,
      errorMessages: {},
      styles: {
        default: {
          color: "#ffffff",
          border: "1px solid #222242",
          borderRadius: "0px",
          padding: "10px 14px",
          fontFamily: "Gilroy-Medium",
          fontSize: "16px",
          background: "#0C0C34",
          height: '45px'
        },
        success: {},
        error: {
          border: "1px solid rgba(255,0,0, 0.3)",
        },
      },
      fonts: [
        {
          fontFamily: 'Gilroy-Medium',
          url: 'https://fonts.cdnfonts.com/s/16219/Gilroy-Medium.woff',
          format: 'woff',
        },
      ],
      onUpdate: function (state: any, binInformation: any, formHasErrors: any) {
        // intentionally empty: required by interface but not used
      },
      onLoad: function () {
        // Optionally add logging or breadcrumbs
      },
      onSubmit: handleFinixSubmit,
      submitLabel: 'Confirm and pay',
    };
    const legacyFinix = getLegacyFinixSDK();
    if (legacyFinix && legacyFinix.CardTokenForm) {
      legacyFinix.CardTokenForm('form-element', options);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Finix Card Form Submission Handler
  async function handleFinixSubmit(err: any, res: any) {
    if (loading) return;
    setLoading(true);
    setError(null);
    if (err) {
      setError('Payment error: ' + (err.message || 'Unknown error'));
      setLoading(false);
      return;
    }
    try {
      const tokenData = res.data || {};
      const token = tokenData.id;
      // Build payload (fill in with actual values from state/context)
      const payload = {
        token,
        tip: Math.round(totalAmount * 100),
        merchantId: appointmentDetails?.technician_details?.finix_merchant, // TODO: Fix property name
        sessionKey: sessionKey, // Set this from Finix session
        type: 'CARD',
        companyName: appointmentDetails?.appointment_details?.CompanyName,
        appointmentId: appointmentDetails?.appointment_details?.AppointmentId,
        idempotencyKey: idempotencyKey,
      };
      await submitTipPayment(payload);
      setSuccess(true);
      // Optionally redirect or update UI
    } catch (e: any) {
      setError('Payment failed: ' + (e.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }

  // Google Pay Integration
  useEffect(() => {
    if (!window.google || !window.google.payments) return;
    // Google Pay client setup
    const paymentsClient = new window.google.payments.api.PaymentsClient({ environment: 'TEST' });
    const button = paymentsClient.createButton({
      onClick: onGooglePayClick,
      buttonColor: 'white',
      buttonType: 'pay',
      buttonSizeMode: 'fill',
    });
    const container = document.getElementById('google-pay-button');
    if (container && container.childElementCount === 0) {
      container.appendChild(button);
    }
    // eslint-disable-next-line
  }, []);

  // Apple Pay Support Check
  useEffect(() => {
    if (window.ApplePaySession && window.ApplePaySession.canMakePayments()) {
      setIsApplePaySupported(true);
    }
  }, []);

  // Google Pay Payment Handler
  async function onGooglePayClick() {
    try {
      setLoading(true);
      setError(null);
      // Google Pay paymentDataRequest
      const paymentsClient = new window.google.payments.api.PaymentsClient({ environment: 'TEST' });
      const paymentDataRequest = {
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: [
          {
            type: 'CARD',
            parameters: {
              allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
              allowedCardNetworks: ['AMEX', 'DISCOVER', 'INTERAC', 'JCB', 'MASTERCARD', 'VISA'],
            },
            tokenizationSpecification: {
              type: 'PAYMENT_GATEWAY',
              parameters: {
                gateway: 'finix',
                gatewayMerchantId: appointmentDetails?.technician_details?.finix_merchant, // TODO: Fix property name
              },
            },
          },
        ],
        transactionInfo: {
          totalPriceStatus: 'FINAL',
          totalPrice: totalAmount.toFixed(2),
          currencyCode: 'USD',
          countryCode: 'US',
        },
        merchantInfo: {
          merchantId: 'MU8tprFug37SUE9Ao9mz74NM', // TODO: use env/config
          merchantName: 'Apruv',
        },
      };
      const paymentData = await paymentsClient.loadPaymentData(paymentDataRequest);
      const token = paymentData.paymentMethodData.tokenizationData.token;
      await submitTipPayment({ token, type: 'GOOGLE_PAY' });
    } catch (err: any) {
      setError('Google Pay failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  // Apple Pay Payment Handler
  async function onApplePayClick() {
    if (!window.ApplePaySession || !window.ApplePaySession.canMakePayments()) {
      setError('Apple Pay is not available on this device/browser.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const paymentRequest = {
        countryCode: 'US',
        currencyCode: 'USD',
        total: {
          label: 'Total Purchase',
          amount: totalAmount.toFixed(2),
        },
        supportedNetworks: ['visa', 'masterCard', 'amex'],
        merchantCapabilities: ['supports3DS'],
      };
      const session = new window.ApplePaySession(6, paymentRequest);
      session.onvalidatemerchant = async (event: any) => {
        // TODO: Implement merchant validation endpoint
        session.completeMerchantValidation({});
      };
      session.onpaymentauthorized = async (event: any) => {
        try {
          const payment = event.payment;
          const token = JSON.stringify(payment);
          await submitTipPayment({ token, type: 'APPLE_PAY' });
          session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
        } catch (err) {
          session.completePayment(window.ApplePaySession.STATUS_FAILURE);
          setError('Apple Pay payment failed.');
        }
      };
      session.oncancel = () => {
        setLoading(false);
      };
      session.begin();
    } catch (err) {
      setError('Apple Pay failed.');
      setLoading(false);
    }
  }

  function updateProcessingFee(tipValue: number, includeFee: boolean) {
    const fee = calculateProcessingFee(tipValue);
    setProcessingFee(fee);
    setTotalAmount(includeFee ? tipValue + Math.round(fee * 100) / 100 : tipValue);
  }

  // --- Payment Submission ---
  async function submitTipPayment({ token, type }: { token: string; type: string }) {
    setLoading(true);
    setError(null);
    try {
      if (!appointmentDetails) throw new Error('Missing appointment details');
      const payload = {
        token: token,
        tip: Math.round(totalAmount * 100), // cents
        merchantId: appointmentDetails.technician_details?.finix_merchant, // TODO: Fix property name
        sessionKey: sessionKey, // TODO: set this from Finix session
        type: type, // 'CARD', 'GOOGLE_PAY', 'APPLE_PAY'
        companyName: appointmentDetails.appointment_details?.CompanyName,
        appointmentId: appointmentDetails.appointment_details?.AppointmentId,
        idempotencyKey: idempotencyKey,
      };
      await submitTipPayment(payload);
      setSuccess(true);
      // Optionally redirect or update UI
      setTimeout(() => {
        goToThankYou();
      }, 1500);
    } catch (err: any) {
      setError('Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  // --- Finix Card Form, Google Pay, Apple Pay logic would call submitTipPayment({ token, type }) ---

  // --- UI ---
  return (
    <div className="payment-container">
      <div className="payment-content">
        {/* Header */}
        <div className="payment-header">
          <button className="payment-back-button" onClick={() => goToTipping()}>
            <i className="pi pi-chevron-left" style={{ fontSize: 20, color: 'var(--color-base-white)' }}></i>
          </button>
          <h2 className="payment-title">Payment Method</h2>
        </div>

        {/* Summary Section */}
        <div className="payment-summary">
          <h3 className="payment-summary-title">Summary</h3>
          <div className="payment-summary-item">
            <span className="payment-summary-label">Tip:</span>
            <span className="payment-summary-value">${tip.toFixed(2)}</span>
          </div>
          <div className="payment-summary-item">
            <span className="payment-summary-label">Payment & payout fees:</span>
            <span className="payment-summary-value">${processingFee.toFixed(2)}</span>
          </div>
          <div className="payment-summary-item" style={{ fontWeight: 600 }}>
            <span className="payment-summary-label">Total:</span>
            <span className="payment-summary-value">${totalAmount.toFixed(2)}</span>
          </div>
          <div className="payment-checkbox mt-2">
            <Checkbox
              inputId="fee-check"
              checked={isFee}
              onChange={e => setIsFee(e.checked as boolean)}
              className="payment-checkbox-button themed-checkbox"
              style={{ borderColor: 'var(--color-primary-600)', background: isFee ? 'var(--color-primary-600)' : 'transparent', borderRadius: 4 }}
            />
            <label htmlFor="fee-check" className="payment-checkbox-label">
              Cover payment & payout fees (${processingFee.toFixed(2)})
            </label>
          </div>
        </div>

        {/* Payment Methods Section */}
        <div className="payment-methods">
          {/* Google Pay Button (official) */}
          <div id="google-pay-button" className="payment-method-button google-pay"></div>

          {/* Apple Pay Button (official) */}
          {isApplePaySupported && (
            <ApplePayButton buttonstyle="black" appleType="pay" locale="en-US" disabled={loading} />
          )}
        </div>

        {/* Section Divider */}
        <div className="payment-section-divider">
          <div className="payment-divider-line"></div>
          <span className="payment-divider-text">Or pay with card</span>
          <div className="payment-divider-line"></div>
        </div>

        {/* Finix Card Form */}
        <div className="payment-form">
          <div id="form-element"></div>
        </div>

        {/* Terms and Policy */}
        <div className="payment-terms mt-4">
          By confirming you agree with our{' '}
          <button className="text-primary underline" type="button" onClick={() => setShowPrivacy(true)}>Privacy Policy</button>{' '}
          and{' '}
          <button className="text-primary underline" type="button" onClick={() => setShowTerms(true)}>Terms of Service</button>.
              </div>

        {/* Loading/Error/Success States */}
        {loading && <div className="text-blue-600 mt-2">Processing payment...</div>}
        {error && <div className="text-red-600 mt-2">{error}</div>}
        {success && <div className="text-green-600 mt-2">Payment successful!</div>}

        <PoweredByApruv sx={{ mt: 'auto' }} />

        {/* Modals for Privacy Policy and Terms */}
        <Dialog
          header={null}
          visible={showPrivacy}
          onHide={() => setShowPrivacy(false)}
          style={{ width: '40rem', maxWidth: '95vw' }}
          closable={false}
          modal
          className="policy-dialog"
          contentClassName="p-0"
          dismissableMask={true}
        >
          <div className="flex justify-between items-center mb-2">
            <span className="font-bold text-lg">Privacy Policy</span>
            <span className="flex gap-2">
              <PrimeButton
                type="button"
                icon="pi pi-times"
                className="p-button-text p-button-rounded text-gray-500 hover:text-primary"
                style={{ fontSize: 22 }}
                aria-label="Close"
                onClick={() => setShowPrivacy(false)}
              />
              {/* Fallback SVG X icon if PrimeIcons fail */}
              <button
                type="button"
                className="p-link p-0 text-gray-500 hover:text-primary"
                style={{ fontSize: 22, display: 'none' }}
                aria-label="Close"
                onClick={() => setShowPrivacy(false)}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 4L16 16M16 4L4 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </span>
          </div>
          <PrivacyPolicyContent />
          <div className="flex justify-end mt-6">
            <PrimeButton label="Close" className="p-button-text text-primary" onClick={() => setShowPrivacy(false)} />
          </div>
        </Dialog>
        <Dialog
          header={null}
          visible={showTerms}
          onHide={() => setShowTerms(false)}
          style={{ width: '40rem', maxWidth: '95vw' }}
          closable={false}
          modal
          className="policy-dialog"
          contentClassName="p-0"
          dismissableMask={true}
        >
          <div className="flex justify-between items-center mb-2">
            <span className="font-bold text-lg">Terms of Service</span>
            <span className="flex gap-2">
              <PrimeButton
                type="button"
                icon="pi pi-times"
                className="p-button-text p-button-rounded text-gray-500 hover:text-primary"
                style={{ fontSize: 22 }}
                aria-label="Close"
                onClick={() => setShowTerms(false)}
              />
              {/* Fallback SVG X icon if PrimeIcons fail */}
              <button
                type="button"
                className="p-link p-0 text-gray-500 hover:text-primary"
                style={{ fontSize: 22, display: 'none' }}
                aria-label="Close"
                onClick={() => setShowTerms(false)}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 4L16 16M16 4L4 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </span>
              </div>
          <TermsOfServiceContent />
          <div className="flex justify-end mt-6">
            <PrimeButton label="Close" className="p-button-text text-primary" onClick={() => setShowTerms(false)} />
          </div>
        </Dialog>
      </div>
    </div>
  );
};

export default PaymentMethod;
